
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoginModal from '../auth/LoginModal';

export default function Navbar() {
  const location = useLocation();
  const { user, subscriberData, isAuthenticated, loading, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLoginSuccess = (loggedInUser: any) => {
    console.log('Login successful:', loggedInUser);
    setIsLoginModalOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };

  const isActivePath = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Format country code to readable name
  const formatCountry = (countryCode: string) => {
    const countryMap: { [key: string]: string } = {
      'USA': 'United States',
      'CAN': 'Canada',
      'GBR': 'United Kingdom',
      'DEU': 'Germany',
      'FRA': 'France',
      'ESP': 'Spain',
      'ITA': 'Italy',
      'NLD': 'Netherlands',
      'SWE': 'Sweden',
      'NOR': 'Norway',
      'DNK': 'Denmark',
      'FIN': 'Finland',
      'AUS': 'Australia',
      'NZL': 'New Zealand',
      'JPN': 'Japan',
      'KOR': 'South Korea',
      'SGP': 'Singapore',
      'HKG': 'Hong Kong',
      'CHE': 'Switzerland',
      'AUT': 'Austria',
      'BEL': 'Belgium',
      'IRL': 'Ireland',
      'PRT': 'Portugal',
      'OTH': 'Other'
    };
    return countryMap[countryCode] || countryCode;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-white shadow-lg z-40">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <i className="ri-mic-line text-white text-xl"></i>
              </div>
              <div className="hidden sm:block">
                <div className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  DBD Podcast
                </div>
                <div className="text-xs text-gray-600 -mt-1">Driven by Data. Delivered by Design.</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`font-medium transition-colors cursor-pointer ${
                  isActivePath('/') 
                    ? 'text-amber-600 border-b-2 border-amber-600 pb-1' 
                    : 'text-gray-700 hover:text-amber-600'
                }`}
              >
                Home
              </Link>
              <Link
                to="/episodes"
                className={`font-medium transition-colors cursor-pointer ${
                  isActivePath('/episodes') 
                    ? 'text-amber-600 border-b-2 border-amber-600 pb-1' 
                    : 'text-gray-700 hover:text-amber-600'
                }`}
              >
                Episodes
              </Link>
              <Link
                to="/about"
                className={`font-medium transition-colors cursor-pointer ${
                  isActivePath('/about') 
                    ? 'text-amber-600 border-b-2 border-amber-600 pb-1' 
                    : 'text-gray-700 hover:text-amber-600'
                }`}
              >
                About
              </Link>
              <Link
                to="/subscribe"
                className={`font-medium transition-colors cursor-pointer ${
                  isActivePath('/subscribe') 
                    ? 'text-amber-600 border-b-2 border-amber-600 pb-1' 
                    : 'text-gray-700 hover:text-amber-600'
                }`}
              >
                Subscribe
              </Link>
              <Link
                to="/contact"
                className={`font-medium transition-colors cursor-pointer ${
                  isActivePath('/contact') 
                    ? 'text-amber-600 border-b-2 border-amber-600 pb-1' 
                    : 'text-gray-700 hover:text-amber-600'
                }`}
              >
                Contact
              </Link>
            </div>

            {/* Auth Section */}
            <div className="flex items-center space-x-4">
              {loading ? (
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                </div>
              ) : isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    {/* Account Icon - Person silhouette in a circle */}
                    <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
                      <i className="ri-user-3-fill text-white text-lg"></i>
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium text-gray-800">
                        {subscriberData?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Subscriber'}
                      </div>
                      <div className="text-xs text-green-600 font-medium">Active Subscriber</div>
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* User Menu Dropdown */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-2xl py-4 z-50">
                      {/* Profile Header */}
                      <div className="px-6 pb-4 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                            <i className="ri-user-3-fill text-white text-xl"></i>
                          </div>
                          <div>
                            <div className="text-lg font-bold text-gray-800">
                              {subscriberData?.name || user?.user_metadata?.name || 'Subscriber'}
                            </div>
                            <div className="text-sm text-gray-600">{subscriberData?.email || user?.email}</div>
                            <div className="text-xs text-green-600 font-medium">✓ Verified Subscriber</div>
                          </div>
                        </div>
                      </div>

                      {/* Subscriber Details */}
                      {subscriberData && (
                        <div className="px-6 py-4 space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500 text-xs font-medium uppercase tracking-wide">Country</div>
                              <div className="text-gray-800 font-medium">{formatCountry(subscriberData.country)}</div>
                            </div>
                            {subscriberData.industry && (
                              <div>
                                <div className="text-gray-500 text-xs font-medium uppercase tracking-wide">Industry</div>
                                <div className="text-gray-800 font-medium">{subscriberData.industry}</div>
                              </div>
                            )}
                            <div>
                              <div className="text-gray-500 text-xs font-medium uppercase tracking-wide">Member Since</div>
                              <div className="text-gray-800 font-medium">{formatDate(subscriberData.subscription_date)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs font-medium uppercase tracking-wide">Episodes Viewed</div>
                              <div className="text-gray-800 font-medium">{subscriberData.total_views || 0}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="px-6 pt-4 border-t border-gray-100">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-logout-circle-line"></i>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-amber-600 transition-colors cursor-pointer"
                  title="Login to your account"
                >
                  <div className="w-8 h-8 border-2 border-gray-300 rounded-full flex items-center justify-center hover:border-amber-500 transition-colors">
                    <i className="ri-login-circle-line text-lg"></i>
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">Login</span>
                </button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden text-gray-700 hover:text-amber-600 transition-colors cursor-pointer"
              >
                <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              <div className="flex flex-col space-y-4">
                <Link
                  to="/"
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium transition-colors cursor-pointer ${
                    isActivePath('/') ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  Home
                </Link>
                <Link
                  to="/episodes"
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium transition-colors cursor-pointer ${
                    isActivePath('/episodes') ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  Episodes
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium transition-colors cursor-pointer ${
                    isActivePath('/about') ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  About
                </Link>
                <Link
                  to="/subscribe"
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium transition-colors cursor-pointer ${
                    isActivePath('/subscribe') ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  Subscribe
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium transition-colors cursor-pointer ${
                    isActivePath('/contact') ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}
